import React, { Component } from "react";
import { Row, Col, Form, ListGroup, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import sha256 from "sha256";
import { uploadImage } from "../../../lib/api/data/Image";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg"];
// New images are scaled down so their longest side is at most this many px.
const MAX_INITIAL_SIZE = 150;

const readImageSize = (url) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
  img.onerror = () => resolve({ width: MAX_INITIAL_SIZE, height: MAX_INITIAL_SIZE });
  img.src = url;
});

// "Upload Image" block shared by the Head, Total and Footer panels: uploads a
// picture, places it on the canvas as `properties.images[key]`, and lists the
// section's images so they can be selected or removed.
//
// Props:
//   section        "head" | "total" | "foot"
//   properties     the section's state (state.template[section])
//   onUpdate       the section's update action (updateHead/updateTotal/updateFoot)
//   findPosition   (width, height) => { top, left } free spot in the section
//   onElementSelected  selects an element on the canvas by id
class SectionImages extends Component {
  state = { uploading: 0 };

  _getImages = () => this.props.properties?.images || {};

  _nextName = () => {
    const used = Object.values(this._getImages())
      .map((img) => parseInt((img?.name || "").replace("image-", ""), 10))
      .filter((n) => !isNaN(n));
    return "image-" + ((used.length ? Math.max(...used) : 0) + 1);
  };

  _setImage = (key, image) => {
    const images = { ...this._getImages() };
    if (image) images[key] = image;
    else delete images[key];
    this.props.onUpdate({ ...this.props.properties, images });
  };

  _onFileSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert(`${file.name} is not a PNG or JPEG image`);
      return;
    }

    // Show the picture straight away from a local URL, then swap in the
    // uploaded URL once the upload finishes.
    const previewUrl = URL.createObjectURL(file);
    const natural = await readImageSize(previewUrl);
    const scale = Math.min(1, MAX_INITIAL_SIZE / Math.max(natural.width, natural.height, 1));
    const width = Math.max(1, Math.round(natural.width * scale));
    const height = Math.max(1, Math.round(natural.height * scale));

    const name = this._nextName();
    const key = name;
    const position = this.props.findPosition(width, height);

    this._setImage(key, {
      id: sha256(this.props.section + "-image-" + name + "-" + Date.now()),
      name,
      src: previewUrl,
      alt: file.name,
      style: {
        position: "absolute",
        top: position.top + "px",
        left: position.left + "px",
        width: width + "px",
        height: height + "px",
      },
    });

    this.setState((s) => ({ uploading: s.uploading + 1 }));

    uploadImage(file)
      .then((result) => {
        const url = result && result.status === 200 && result.data && result.data.Result;
        if (!url) throw new Error("Upload response did not include an image URL");

        const current = this._getImages()[key];
        if (current) this._setImage(key, { ...current, src: url });
      })
      .catch((err) => {
        console.error(err);
        if (this._getImages()[key]) this._setImage(key, null);
        alert(`${file.name} couldn't be uploaded. Please try again.`);
      })
      .finally(() => {
        URL.revokeObjectURL(previewUrl);
        this.setState((s) => ({ uploading: Math.max(0, s.uploading - 1) }));
      });
  };

  _renderList = () => {
    const images = this._getImages();

    return Object.keys(images).map((key) => {
      const data = images[key];
      if (!data) return null;

      return (
        <ListGroup.Item
          className="d-flex justify-content-between align-items-center lh-condensed bg-white"
          data-target={data.id}
          data-section={this.props.section}
          data-path="images"
          data-index={key}
          key={key}
          onClick={() => this.props.onElementSelected && this.props.onElementSelected(data.id)}
        >
          <div className="d-flex align-items-center" style={{ gap: "8px", minWidth: 0 }}>
            <img
              src={data.src}
              alt={data.alt || data.name}
              style={{ width: "32px", height: "32px", objectFit: "contain", flexShrink: 0 }}
            />
            <h6 className="my-0 text-truncate" title={data.alt || data.name}>
              {data.name}
            </h6>
          </div>
          <span className="text-muted">
            <Button
              variant="link"
              title="Remove image"
              onClick={(e) => {
                e.stopPropagation();
                this._setImage(key, null);
              }}
            >
              <FontAwesomeIcon icon="minus" />
            </Button>
          </span>
        </ListGroup.Item>
      );
    });
  };

  render() {
    const hasImages = Object.keys(this._getImages()).length > 0;

    return (
      <Row>
        <Col>
          <div className="fw-bold mt-3">Upload Image</div>
          <Form.Control
            type="file"
            size="sm"
            className="mt-2"
            accept="image/png, image/jpeg, image/jpg"
            onChange={this._onFileSelected}
          />
          <Form.Text className="text-muted">
            PNG or JPEG. The image is added to the {this.props.section === "head" ? "header" : this.props.section === "foot" ? "footer" : "total"} section; drag or resize it on the canvas.
            {this.state.uploading > 0 && " Uploading…"}
          </Form.Text>

          {hasImages && (
            <ListGroup
              className="mt-2 mb-3 border-1"
              style={{ maxHeight: "300px", overflowY: "auto", overflowX: "hidden" }}
            >
              {this._renderList()}
            </ListGroup>
          )}
        </Col>
      </Row>
    );
  }
}

export default SectionImages;
